import React, { useEffect, useState } from 'react';
import SectionedDialog from './SectionedDialog';

/** Fetch + render the published agent_spec for a station. Opened from
 *  NodeDetail's per-station "view spec" hamburger item. Calls
 *  GET /api/stations/{id}/spec which proxies to hub GET /s/{id}; the
 *  spec is JSON-Schema-ish ({function_sets: [{functions: [{parameters:
 *  {properties}}]}]}) — any @Helper-decorated module emits this shape
 *  at boot via aveq_link.helper. 404 means the helper hasn't published
 *  yet.
 *
 *  admin-sectioned-dialogs slug: read-only SectionedDialog — each function
 *  set is a category (plus an Overview and a Raw JSON pane). No primary
 *  action, so the footer is a single Close. */
export default function StationSpecModal({ stationId, stationName, stationConfig, onClose }) {
  const [data, setData] = useState(null);  // {spec, updated} or null
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeCat, setActiveCat] = useState('overview');
  // A single raw/structured toggle, honored by every category's pane.
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/api/stations/${stationId}/spec`);
        const text = await r.text();
        let parsed = null;
        try { parsed = JSON.parse(text); } catch (_) {}
        if (r.status === 404) { if (!cancelled) setNotFound(true); return; }
        if (!r.ok) throw new Error((parsed && (parsed.message || parsed.error)) || text || `HTTP ${r.status}`);
        if (!cancelled) setData(parsed && parsed.entity ? parsed.entity : parsed);
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [stationId]);

  const spec = data && data.spec;
  const updated = data && data.updated;
  const functionSets = (spec && spec.function_sets) || [];

  // Each category pane gets a raw/structured toggle at the top; raw shows the
  // JSON for THAT pane's data (the whole spec for Overview, the one set for a
  // function-set pane). The toggle is shared so the mode persists as you navigate.
  const paneWithRaw = (rawData, structured) => (
    <>
      <div style={{ marginBottom: 10 }}>
        <button type="button" onClick={() => setShowRaw((v) => !v)} style={rawToggleStyle}>
          {showRaw ? '◂ structured view' : 'raw JSON ▸'}
        </button>
      </div>
      {showRaw ? <pre style={rawJsonStyle}>{JSON.stringify(rawData, null, 2)}</pre> : structured}
    </>
  );

  // Build the category list from the current state. Loading / error / not-found
  // collapse to a single status pane; a loaded spec fans out into Overview +
  // one category per function set + Raw JSON.
  let categories;
  if (loading) {
    categories = [{ id: 'overview', label: 'Spec', render: () => <div style={{ color: '#888' }}>Loading…</div> }];
  } else if (error) {
    categories = [{ id: 'overview', label: 'Spec', render: () => <div style={{ color: '#c00', fontSize: '0.85rem' }}>{error}</div> }];
  } else if (notFound) {
    categories = [{ id: 'overview', label: 'Spec', render: () => (
      <div style={{ color: '#888', fontSize: '0.9rem' }}>
        No spec published. Helpers register on first boot — has this
        station's helper run yet?
      </div>
    ) }];
  } else {
    categories = [
      {
        id: 'overview',
        label: 'Overview',
        description: updated ? `Published spec · updated ${fmtRelative(updated)}` : 'Published spec',
        render: () => (
          <>
            <HelpersSummary stationConfig={stationConfig} />
            <div style={{ fontSize: '0.85rem', color: '#444' }}>
              {functionSets.length} function set{functionSets.length === 1 ? '' : 's'}.
              {functionSets.length === 0 && ' This spec has no function_sets.'}
            </div>
          </>
        ),
      },
      ...functionSets.map((fs, i) => {
        const n = (fs.functions || []).length;
        return {
          id: `fs_${i}`,
          label: fs.name,
          description: fs.description || `${n} function${n === 1 ? '' : 's'}`,
          render: () => paneWithRaw(fs, <FunctionSetDetail fs={fs} />),
        };
      }),
    ];
  }

  return (
    <SectionedDialog
      title={`Spec — ${stationName || `station ${stationId}`}`}
      categories={categories}
      activeId={activeCat}
      onSelect={setActiveCat}
      onCancel={onClose}
      cancelLabel="Close"
      width={760}
    />
  );
}

/** One-line summary of the station's helper policy from
 *  station.config.helpers (station-helper-gating slug). Renders
 *  nothing when no policy is set AND no legacy `*_enabled` keys
 *  linger — the common case for "use defaults." */
function HelpersSummary({ stationConfig }) {
  const cfg = stationConfig || {};
  const helpers = cfg.helpers || {};
  const included = Array.isArray(helpers.included) && helpers.included.length > 0
    ? helpers.included : null;
  const excluded = Array.isArray(helpers.excluded) && helpers.excluded.length > 0
    ? helpers.excluded : null;
  // Mirror of aveq-hub/web_entry.py _DEAD_ENABLED_FLAGS. tobin_enabled
  // stays — Tobin-restaurant marker, not a helper toggle.
  const legacy = ['sensor_enabled', 'hiring_service_enabled',
                  'tobin_suika_enabled', 'job_listing_enabled']
    .filter((k) => Object.prototype.hasOwnProperty.call(cfg, k));

  if (!included && !excluded && legacy.length === 0) return null;

  return (
    <div style={helpersSummaryStyle}>
      <span style={{ fontWeight: 500, marginRight: 6 }}>Helpers:</span>
      {included && <span>only <code>{included.join(', ')}</code></span>}
      {excluded && <span>all except <code>{excluded.join(', ')}</code></span>}
      {!included && !excluded && (
        <span style={{ color: '#888' }}>default (all node helpers)</span>
      )}
      {legacy.length > 0 && (
        <span style={{ color: '#a06000', marginLeft: 8, fontSize: '0.75rem' }}>
          (legacy <code>{legacy.join(', ')}</code> — will be cleaned up on next Edit save)
        </span>
      )}
    </div>
  );
}

/** The body of one function set (its name + description ride in the category
 *  header now). Renders the starting-point badge, labels, action_uri, then the
 *  per-function detail. */
function FunctionSetDetail({ fs }) {
  const functions = fs.functions || [];
  return (
    <div>
      {fs.starting_point && (
        <div style={{ marginBottom: 8 }}><span style={badgeStyle}>starting_point</span></div>
      )}
      {fs.labels && fs.labels.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          {fs.labels.map((l, i) => <span key={i} style={chipStyle}>{l}</span>)}
        </div>
      )}
      {fs.action_uri && (
        <div style={{ ...metaStyle, fontFamily: 'monospace', marginBottom: 8 }}>
          action_uri: {fs.action_uri}
        </div>
      )}
      {functions.length === 0 ? (
        <div style={{ ...metaStyle, fontStyle: 'italic' }}>(no functions)</div>
      ) : functions.map((fn, i) => <FunctionBlock key={i} fn={fn} />)}
    </div>
  );
}

function FunctionBlock({ fn }) {
  const params = (fn.parameters && fn.parameters.properties) || {};
  const required = new Set((fn.parameters && fn.parameters.required) || []);
  const paramNames = Object.keys(params);
  return (
    <div style={fnBlockStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <code style={fnNameStyle}>{fn.name}</code>
        {fn.return && (
          <span style={returnPillStyle}>
            {/* spec-to-interface: a result CONTRACT (an object with `kind`) reads as a
                list of that kind; a string stays the type name it always was. */}
            → {typeof fn.return === 'object' ? `list of ${fn.return.kind || fn.return.type || 'record'}` : fn.return}
          </span>
        )}
      </div>
      {fn.description && (
        <div style={{ fontSize: '0.8rem', color: '#444', margin: '4px 0 6px' }}>
          {fn.description}
        </div>
      )}
      {paramNames.length === 0 ? (
        <div style={{ ...metaStyle, fontStyle: 'italic' }}>(no parameters)</div>
      ) : (
        <table style={paramTableStyle}>
          <thead>
            <tr style={{ color: '#888', textAlign: 'left' }}>
              <th style={paramThStyle}>name</th>
              <th style={paramThStyle}>type</th>
              <th style={paramThStyle}>req</th>
              <th style={paramThStyle}>description</th>
            </tr>
          </thead>
          <tbody>
            {paramNames.map((name) => {
              const p = params[name] || {};
              return (
                <tr key={name}>
                  <td style={paramTdStyle}><code>{name}</code></td>
                  <td style={paramTdStyle}>{p.type || '—'}</td>
                  <td style={paramTdStyle}>{required.has(name) ? 'yes' : ''}</td>
                  <td style={paramTdStyle}>{p.description || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function fmtRelative(iso) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diffSec = (Date.now() - t) / 1000;
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

const metaStyle = { color: '#888', fontSize: '0.78rem' };
const badgeStyle = {
  padding: '1px 6px', fontSize: '0.7rem',
  background: '#dff0e0', color: '#2a7a3a', borderRadius: 3,
};
const chipStyle = {
  display: 'inline-block', marginRight: 4, padding: '1px 6px',
  fontSize: '0.7rem', background: '#eee', color: '#555', borderRadius: 3,
};
const fnBlockStyle = {
  borderLeft: '3px solid #5fa67a', paddingLeft: 10, marginTop: 8, marginBottom: 4,
};
const fnNameStyle = {
  fontSize: '0.88rem', fontWeight: 600, background: '#f0f0f0',
  padding: '1px 5px', borderRadius: 3,
};
const returnPillStyle = {
  fontSize: '0.7rem', color: '#666', fontFamily: 'monospace',
};
const paramTableStyle = {
  fontSize: '0.78rem', borderCollapse: 'collapse', width: '100%',
};
const paramThStyle = {
  fontWeight: 500, padding: '3px 8px 3px 0', borderBottom: '1px solid #e8e8e8',
};
const paramTdStyle = {
  padding: '3px 8px 3px 0', verticalAlign: 'top', borderBottom: '1px solid #f4f4f4',
};
const rawJsonStyle = {
  fontSize: '0.75rem', background: '#f8f8f8', padding: 10, borderRadius: 4,
  overflow: 'auto', border: '1px solid #eee', margin: 0,
};
const rawToggleStyle = {
  background: 'none', border: 'none', color: '#1a73e8', cursor: 'pointer',
  padding: 0, fontSize: '0.75rem',
};
const helpersSummaryStyle = {
  marginBottom: 12, padding: '8px 10px',
  background: '#f5f8f6', border: '1px solid #d8e5dc', borderRadius: 4,
  fontSize: '0.85rem', color: '#333',
};
